/**
 * Обозреватель референсов: плитка, выделение, панель действий и их мутации.
 *
 * Один на экран библиотеки, «Все референсы» и корзину: экран даёт строки и
 * ключ их кэша, обозреватель отвечает за выделение и за то, как действие
 * ложится в кэш — корзина патчится на месте (плитка не прыгает), удаление
 * перезапрашивает всё («library»), потому что строка могла быть в любом срезе.
 * Теги и вердикт тоже патчатся на месте; перенос и копия перезапрашивают всё.
 *
 * В режиме `library` над плиткой фильтры по тегу и вердикту — на клиенте, по
 * одному ответу экрана: «все видимые» и Shift-диапазон иначе не определены.
 * Число у фишки тега — по срезу после вердикта.
 *
 * Только в `library`: фото одной модели (ключ — из заметки, `lib/models.ts`)
 * собираются в папки. Адрес: `?model=<ключ>` — открытая папка (push в историю),
 * `?view=photos` — «все фото» (умолчание «папками» в адрес не пишется; переключение
 * — replace и снимает `model`). Порядок среза: вердикт → тег → группировка. Выделение
 * — набор id и живёт между папками; «все видимые» берёт то, что в сетке сейчас
 * (фото папки в срезе, на уровне папок — только фото без модели). Сетка рисуется
 * порциями по `PAGE`; к первой порции её возвращает `key` — вид, папка, фильтры и
 * `resetKey` экрана (серверные фильтры «Всех референсов» из адреса). Открывая папку,
 * запоминаем прокрутку `main` и число нарисованных плиток уровня папок; вернулись
 * к тому же уровню (тот же срез) — рисуем столько же и ставим прокрутку назад до
 * отрисовки: плитки — коробки с заданной пропорцией, высота известна до картинок.
 * В `all` и корзине `model`/`view` не читаются — кроме выдачи поиска по фото (`scores`):
 * там папки в любом режиме, их порядок — порядок строк (экран даёт их по убыванию score,
 * значит, папка — по лучшему фото), на плитках — процент сходства.
 *
 * «клик: открыть | выделить» — что делает клик по плитке (папке и фото), помнит `localStorage`.
 * «Генерировать» в панели — выделенные id (у папки — обложка) референсами в `/generate`.
 * Фильтр «генерация: была | не было» — по счётчику `generations`, шаг среза между вердиктом и тегом.
 * В открытой папке — лента генераций по всем фото модели (`FolderGenerations`, мимо фильтров).
 * Удаление на уровне папок: выделенная обложка — все фото модели среди `items`, мимо фильтров
 * (`deleteIds`); в подтверждении — число папок, фото и генераций по всем удаляемым.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Pencil, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

import { FittingFeed } from "@/components/fitting/FittingFeed";
import { parseBrowserView } from "@/features/library/model/browserView";
import {
  autoGroups as autoGroupsOf,
  beforeTagSlice,
  byVerdict as byVerdictOf,
  countTags,
  sliceItems,
  verdictCounts as verdictCountsOf,
  type TagFilter,
  type VerdictFilter,
} from "@/features/library/model/refSlice";
import { ReferenceGrid } from "@/components/library/ReferenceGrid";
import { ReferenceSelectionBar } from "@/components/library/ReferenceSelectionBar";
import { SiteTile } from "@/components/library/SiteTile";
import { TagManager } from "@/components/trends/TagManager";
import { btnGhost, Chip, Empty, Panel, Segmented } from "@/components/trends/ui";
import { byMarks, groupMissing, groupTagIds, patchManual, type Marks } from "@/lib/autotag";
import { copyText, csvName, downloadBlob, idsText, itemsCsv } from "@/lib/exportList";
import { MAX_PAIRS } from "@/lib/fitting";
import { generateHref } from "@/lib/generateLink";
import {
  basketAdd,
  basketRemove,
  copyItems,
  deleteItems,
  libraryKeys,
  loadSites,
  moveItems,
  patchItems,
  plural,
  setVerdict,
  tagItems,
  untagItems,
  type Verdict,
} from "@/lib/library";
import { num } from "@/lib/media";
import { deleteIds, folderLevelShown, groupByModel, modelOf, outsideLoose, selectedFolders } from "@/lib/models";
import { useSelection } from "@/lib/selection";
import { foundSites, LEVEL_LABEL, LEVELS, siteItems, sitesCaption, type SiteLevel } from "@/lib/sites";
import { usedCounts, type UsedFilter } from "@/lib/usedFilter";
import { blankTag, loadTags, tagKeys, tagStyle, type Tag, type TagBy, type TagsData } from "@/lib/tags";

import type { ReferenceItem } from "@/lib/library";

const EMPTY_TAGS: Tag[] = [];

/** Что делает клик по плитке; выбор помнит браузер (общий для всех экранов референсов). */
type ClickMode = "open" | "select";
const CLICK_KEY = "reels.refs.click";
function readClick(): ClickMode {
  try {
    return window.localStorage.getItem(CLICK_KEY) === "select" ? "select" : "open";
  } catch {
    return "open";
  }
}

/** Лента генераций по всем фото открытой модели; нет генераций — одна строка и запроса нет. */
function FolderGenerations({ items }: { items: ReferenceItem[] }) {
  const [open, setOpen] = useState(true);
  const total = items.reduce((sum, i) => sum + i.generations, 0);
  if (total === 0)
    return <div className="border-b border-border px-3 py-2 text-[12px] text-fg-faint">по фото этой модели ещё не генерировали</div>;
  return (
    <div className="border-b border-border" data-annot="генерации модели">
      <button
        type="button"
        className="flex w-full items-center gap-1 px-3 py-2 text-left text-[12.5px] font-medium"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        Генерации по фото этой модели <span className="tnum text-fg-faint">{num(total)}</span>
      </button>
      {open ? (
        <div className="px-3 pb-3">
          <FittingFeed filter={{ refs: items.map((i) => i.id) }} emptyText="Генераций не нашлось" />
        </div>
      ) : null}
    </div>
  );
}

export function ReferenceBrowser({
  items,
  mode,
  queryKey,
  emptyText,
  title,
  resetKey = "",
  scores,
  percent = true,
  libraryId,
}: {
  items: ReferenceItem[];
  mode: "library" | "all" | "basket";
  queryKey: readonly unknown[];
  emptyText: ReactNode;
  title?: ReactNode;
  /** Срез экрана (серверные фильтры): смена — снова первая порция сетки. */
  resetKey?: string;
  /** Выдача поиска по фото: id → score; задан — папки моделей в любом режиме и бейджи процента. */
  scores?: ReadonlyMap<number, number>;
  /** `false` — выдача по описанию: порядок серверный, процентов на плитках нет. */
  percent?: boolean;
  /** Только на экране библиотеки: даёт третий вид «площадки» (`?view=sites`). */
  libraryId?: number;
}) {
  const queryClient = useQueryClient();
  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const tags = tagsQuery.data?.tags ?? EMPTY_TAGS;
  const tagNames = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags]);

  const filterable = mode === "library";
  const [tagFilter, setTagFilterState] = useState<TagFilter>(null);
  // «метки: все · ручные · авто» — уточнение выбранного тега; другой тег — снова «все».
  const [marks, setMarks] = useState<Marks>("all");
  const setTagFilter = (f: TagFilter) => {
    setTagFilterState(f);
    setMarks("all");
  };
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>("all");
  const [usedFilter, setUsedFilter] = useState<UsedFilter>("all");
  const [managing, setManaging] = useState(false);
  const [lastLibraryId, setLastLibraryId] = useState<number | null>(null);

  // Срез по шагам: вердикт → генерация → тег; число у фишки тега — по срезу ДО тега.
  const byVerdict = useMemo(() => byVerdictOf(items, filterable, verdictFilter), [items, filterable, verdictFilter]);
  const beforeTag = useMemo(
    () => beforeTagSlice(byVerdict, filterable, usedFilter),
    [byVerdict, filterable, usedFilter],
  );
  const tagCountsInSlice = useMemo(() => countTags(beforeTag), [beforeTag]);
  const untagged = useMemo(() => beforeTag.filter((i) => i.tag_ids.length === 0).length, [beforeTag]);
  // Группы с автотегами: фишка «‹Группа›: без тега N» — фото без единого тега группы (их отсёк порог).
  const autoGroups = useMemo(() => autoGroupsOf(tags), [tags]);
  const groupMissingCounts = useMemo(
    () => new Map(autoGroups.map((g) => [g.id, groupMissing(beforeTag, g.tagIds).length])),
    [autoGroups, beforeTag],
  );
  const filterTag = typeof tagFilter === "number" ? (tags.find((t) => t.id === tagFilter) ?? null) : null;
  const filterGroup =
    typeof tagFilter === "string" && tagFilter !== "none"
      ? (autoGroups.find((g) => `g${g.id}` === tagFilter) ?? null)
      : null;
  const markCounts = useMemo(() => {
    if (typeof tagFilter !== "number") return null;
    return {
      all: byMarks(beforeTag, tagFilter, "all").length,
      manual: byMarks(beforeTag, tagFilter, "manual").length,
      auto: byMarks(beforeTag, tagFilter, "auto").length,
    };
  }, [beforeTag, tagFilter]);
  // «метки» — только у автотега или у тега с автометками в срезе: у ручного тега выбор пуст.
  const showMarks = markCounts !== null && (filterTag?.prompt_en != null || markCounts.auto > 0 || marks !== "all");
  const sliced = useMemo(
    () => sliceItems(beforeTag, filterable, tagFilter, marks, filterGroup),
    [beforeTag, filterable, tagFilter, marks, filterGroup],
  );
  const used = useMemo(() => usedCounts(items), [items]);
  const verdictCounts = useMemo(() => verdictCountsOf(items), [items]);

  // Папки моделей — в `library` и в выдаче поиска; хук зовём всегда, значения берём при `folderable`.
  const folderable = filterable || scores !== undefined;
  const [params, setParams] = useSearchParams();
  const hasModels = useMemo(() => folderable && items.some((i) => modelOf(i.note)), [items, folderable]);
  const sitesOn = filterable && scores === undefined && libraryId !== undefined;
  const { view, modelKey, level, siteNumber } = parseBrowserView(params, sitesOn, hasModels);
  const sitesQuery = useQuery({
    queryKey: libraryKeys.sites(libraryId ?? 0, level),
    queryFn: () => loadSites(libraryId as number, level),
    enabled: view === "sites" && libraryId !== undefined,
  });
  const sitesData = sitesQuery.data;
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const slicedById = useMemo(() => new Map(sliced.map((i) => [i.id, i])), [sliced]);
  const openSite = siteNumber !== null ? (sitesData?.sites.find((s) => s.number === siteNumber) ?? null) : null;
  const grouped = useMemo(() => groupByModel(sliced), [sliced]);
  // Шапке папки нужны имя и коллекция модели — по первому её фото среди всех, не по срезу.
  const knownModel = useMemo(() => {
    if (!modelKey) return null;
    for (const i of items) {
      const model = modelOf(i.note);
      if (model?.key === modelKey) return model;
    }
    return null;
  }, [items, modelKey]);
  // Все фото открытой модели, мимо фильтров, — для ленты её генераций.
  const modelItems = useMemo(
    () => (modelKey ? items.filter((i) => modelOf(i.note)?.key === modelKey) : []),
    [items, modelKey],
  );
  const openFolder = modelKey ?(grouped.folders.find((f) => f.key === modelKey) ?? null) : null;
  const gridFolders = view === "folders" && !modelKey ? grouped.folders : undefined;
  const gridItems =
    view === "sites"
      ? openSite
        ? siteItems(openSite, slicedById)
        : []
      : !folderable || view === "photos"
        ? sliced
        : modelKey
          ? (openFolder?.items ?? [])
          : grouped.loose;
  const withParams = (edit: (next: URLSearchParams) => void, replace: boolean) => {
    const next = new URLSearchParams(params);
    edit(next);
    setParams(next, replace ? { replace: true } : undefined);
  };
  const pickView = (v: "folders" | "photos" | "sites") =>
    withParams((next) => {
      if (v === "folders") next.delete("view");
      else next.set("view", v);
      next.delete("model");
      if (v !== "sites") {
        next.delete("site");
        next.delete("site_level");
      }
    }, true);
  const toFolders = () => withParams((next) => next.delete("model"), false);
  const pickLevel = (l: SiteLevel) =>
    withParams((next) => {
      if (l === "normal") next.delete("site_level");
      else next.set("site_level", l);
      next.delete("site");
    }, true);
  const openSiteNumber = (n: number) => withParams((next) => next.set("site", String(n)), false);
  const toSites = () => withParams((next) => next.delete("site"), false);

  // Ключ сетки — сброс к первой порции; `folderLevelKey` — тот же срез на уровне папок.
  const sliceKey = [verdictFilter, usedFilter, tagFilter, marks];
  const gridKey = [resetKey, view, modelKey ?? siteNumber ?? "", level, ...sliceKey].join("|");
  const folderLevelKey = [resetKey, view, "", ...sliceKey].join("|");
  // Место на уровне папок до открытия папки; библиотека в ключе — другая библиотека не наследует.
  const placeKey = `${JSON.stringify(queryKey)}|${folderLevelKey}`;
  const gridRoot = useRef<HTMLDivElement>(null);
  const place = useRef<{ key: string; limit: number; scrollTop: number } | null>(null);
  const openModel = (key: string, limit: number) => {
    place.current = { key: placeKey, limit, scrollTop: gridRoot.current?.closest("main")?.scrollTop ?? 0 };
    withParams((next) => next.set("model", key), false);
  };
  const back = !modelKey && place.current?.key === placeKey ? place.current : null;
  // Вернулись к папкам: сетка уже нарисована на `back.limit` плиток — ставим прокрутку до
  // отрисовки кадра. Срез сменился (другой фильтр, другая библиотека) — место забываем.
  useLayoutEffect(() => {
    const saved = place.current;
    if (modelKey || !saved) return;
    const main = gridRoot.current?.closest("main");
    if (saved.key === placeKey && main) main.scrollTop = saved.scrollTop;
    place.current = null;
  }, [modelKey, placeKey]);

  // На уровне папок выделяются обложки папок, потом россыпь; стабильная ссылка — иначе якорь Shift сбрасывается.
  const shown = useMemo(() => (gridFolders ? folderLevelShown(gridFolders, gridItems) : gridItems), [gridFolders, gridItems]);
  const { selected, selecting, setSelecting, toggle, selectVisible, clear } = useSelection(
    items,
    shown,
    !managing,
    readClick() === "select",
  );
  const pickClick = (v: ClickMode) => {
    setSelecting(v === "select");
    try {
      window.localStorage.setItem(CLICK_KEY, v);
    } catch {
      // хранилище закрыто — выбор живёт до перезагрузки
    }
  };
  const [result, setResult] = useState<string | null>(null);
  // Итог переноса/копии: в панели его не видно — перенесённые выпадают из выделения.
  const [notice, setNotice] = useState<string | null>(null);
  // Началось новое выделение — прежний итог устарел.
  useEffect(() => {
    if (selected.size > 0) setNotice(null);
  }, [selected.size]);
  // Выделение опустело — итог прошлого действия не должен вернуться со следующим.
  useEffect(() => {
    if (selected.size === 0) setResult(null);
  }, [selected.size]);
  const onClear = () => {
    clear();
    setResult(null);
  };

  // Удалили тег, по которому стоял фильтр (или в группе не осталось автотегов), — фильтр сбрасывается.
  const filterGone =
    tagsQuery.isSuccess &&
    ((typeof tagFilter === "number" && !tagNames.has(tagFilter)) ||
      (typeof tagFilter === "string" && tagFilter !== "none" && filterGroup === null));
  useEffect(() => {
    if (!filterGone) return;
    setTagFilterState(null);
    setMarks("all");
  }, [filterGone]);

  const patchBasket = (ids: number[], inBasket: boolean) => {
    const set = new Set(ids);
    queryClient.setQueryData(queryKey, (d: { items: ReferenceItem[] } | undefined) =>
      patchItems(d, set, (i) => ({ ...i, in_basket: inBasket })),
    );
    // В корзине убранная строка должна уйти с экрана — перезапрос, а не патч.
    if (mode === "basket") void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: libraryKeys.basket });
  };

  const basket = useMutation({
    mutationFn: async ({ ids, add }: { ids: number[]; add: boolean }) => {
      if (add) {
        const got = await basketAdd(ids);
        return { ids, add, text: `в корзину: добавлено ${num(got.added)}, уже было ${num(got.already)}` };
      }
      const got = await basketRemove(ids);
      return { ids, add, text: `убрано ${num(got.removed)}` };
    },
    onMutate: () => setResult(null),
    onSuccess: ({ ids, add, text }) => {
      patchBasket(ids, add);
      setResult(text);
    },
  });

  // Ручная метка правит и `tag_ids`, и `auto_tag_ids` (ручная главнее, вытесняет автометки группы).
  const patchTag = (ids: number[], tagId: number, add: boolean) => {
    const set = new Set(ids);
    const mates = groupTagIds(tags, tagId);
    queryClient.setQueryData(queryKey, (d: { items: ReferenceItem[] } | undefined) =>
      patchItems(d, set, (i) => patchManual(i, tagId, add, mates)),
    );
    void queryClient.invalidateQueries({ queryKey: tagKeys.list });
  };

  const tagMut = useMutation({
    mutationFn: ({ ids, by }: { ids: number[]; by: TagBy }) => tagItems(ids, by),
    onSuccess: (got, { ids }) => {
      patchTag(ids, got.tag.id, true);
      if (got.tag.created)
        // Новый тег — сразу в словарь, чтобы бейдж не мелькнул «#id» до перезапроса.
        queryClient.setQueryData<TagsData>(tagKeys.list, (d) =>
          d ? { ...d, tags: [...d.tags, blankTag(got.tag.id, got.tag.name)] } : d,
        );
    },
  });
  const untagMut = useMutation({
    mutationFn: ({ ids, tagId }: { ids: number[]; tagId: number }) => untagItems(ids, tagId),
    onSuccess: (_got, { ids, tagId }) => patchTag(ids, tagId, false),
  });
  const verdictMut = useMutation({
    mutationFn: ({ ids, verdict }: { ids: number[]; verdict: Verdict | null }) => setVerdict(ids, verdict),
    onSuccess: (_got, { ids, verdict }) => {
      queryClient.setQueryData(queryKey, (d: { items: ReferenceItem[] } | undefined) =>
        patchItems(d, new Set(ids), (i) => ({ ...i, verdict })),
      );
    },
  });

  const afterShift = (libraryId: number, text: string) => {
    setLastLibraryId(libraryId);
    // Перенесённые уходят из выделения и панель исчезает — итог живёт вне панели.
    setNotice(text);
    void queryClient.invalidateQueries({ queryKey: ["library"] });
    void queryClient.invalidateQueries({ queryKey: libraryKeys.list });
    void queryClient.invalidateQueries({ queryKey: ["project"] });
  };
  const move = useMutation({
    mutationFn: async ({ ids, libraryId }: { ids: number[]; libraryId: number }) => ({
      libraryId,
      got: await moveItems(ids, libraryId),
    }),
    onMutate: () => setNotice(null),
    onSuccess: ({ libraryId, got }) =>
      afterShift(libraryId, `переложено ${num(got.moved)}, уже было ${num(got.already)}`),
  });
  const copy = useMutation({
    mutationFn: async ({ ids, libraryId }: { ids: number[]; libraryId: number }) => ({
      libraryId,
      got: await copyItems(ids, libraryId),
    }),
    onMutate: () => setNotice(null),
    onSuccess: ({ libraryId, got }) =>
      afterShift(libraryId, `скопировано ${num(got.copied)}, уже было ${num(got.already)}`),
  });

  const remove = useMutation({
    mutationFn: (ids: number[]) => deleteItems(ids),
    onMutate: () => setResult(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["library"] });
      void queryClient.invalidateQueries({ queryKey: libraryKeys.list });
      void queryClient.invalidateQueries({ queryKey: ["project"] });
    },
  });

  // Сколько выделенных уже несут каждый тег — «на k из N» в поповере.
  const selectedTagCounts = useMemo(() => countTags(items.filter((i) => selected.has(i.id))), [items, selected]);

  const chosen = items.filter((i) => selected.has(i.id));
  // Все выбранные уже в корзине — кнопка убирает, иначе кладёт.
  const basketMode = chosen.length > 0 && chosen.every((i) => i.in_basket) ? "remove" : "add";
  const ids = chosen.map((i) => i.id);
  // Удаление: обложка папки — вся модель; подтверждению — папки, фото и их генерации.
  const removeIds = deleteIds(selected, gridFolders, items);
  const removeSet = new Set(removeIds);
  const toDelete = {
    folders: selectedFolders(selected, gridFolders).length,
    photos: removeIds.length,
    generations: items.reduce((sum, i) => sum + (removeSet.has(i.id) ? i.generations : 0), 0),
  };

  return (
    <Panel
      title={
        title ?? (
          <>
            Референсы <span className="tnum text-fg-faint">{num(items.length)}</span>
          </>
        )
      }
      annot="референсы библиотеки"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <Segmented<ClickMode>
          label="клик"
          value={selecting ? "select" : "open"}
          onPick={pickClick}
          options={[
            { value: "open", label: "открыть", title: "Клик открывает папку или оригинал (Ctrl/Shift — выделить)" },
            { value: "select", label: "выделить", title: "Клик выделяет папку или фото (Shift — диапазон)" },
          ]}
        />
        <span className="text-[12px] text-fg-faint tnum">
          {filterable && sliced.length !== items.length
            ? `${num(sliced.length)} из ${num(items.length)}`
            : num(items.length)}
        </span>
        {hasModels || sitesOn ? (
          <Segmented<"folders" | "photos" | "sites">
            label="вид"
            value={view}
            onPick={pickView}
            options={
              scores
                ? [
                    { value: "folders", label: "модели" },
                    { value: "photos", label: "фото" },
                  ]
                : [
                    ...(hasModels ? [{ value: "folders" as const, label: "папками" }] : []),
                    { value: "photos" as const, label: "все фото" },
                    ...(sitesOn ? [{ value: "sites" as const, label: "площадки" }] : []),
                  ]
            }
          />
        ) : null}
        {gridFolders ? (
          <span className="text-[12px] text-fg-faint tnum">
            {`${num(gridFolders.length)} ${plural(gridFolders.length, "модель", "модели", "моделей")}`}
          </span>
        ) : null}
        {notice ? (
          <span
            className="inline-flex items-center gap-1 rounded-[var(--radius-ui)] bg-ok-bg px-2 py-0.5 text-[12px] text-ok"
            role="status"
          >
            {notice}
            <button type="button" aria-label="Скрыть" className="grid place-items-center" onClick={() => setNotice(null)}>
              <X size={12} />
            </button>
          </span>
        ) : null}
      </div>

      {view === "sites" && items.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2" data-annot="площадки">
          <Segmented<SiteLevel>
            label="уровень"
            value={level}
            onPick={pickLevel}
            options={LEVELS.map((l) => ({ value: l, label: LEVEL_LABEL[l] }))}
          />
          {sitesData ? <span className="text-[12px] text-fg-faint tnum">{sitesCaption(sitesData)}</span> : null}
        </div>
      ) : null}
      {view === "sites" && openSite && items.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2" data-annot="шапка площадки">
          <button type="button" className={btnGhost} onClick={toSites}>
            ← все площадки
          </button>
          <span className="text-[12.5px]">
            Площадка {openSite.number} · {num(gridItems.length)} фото
          </span>
        </div>
      ) : null}

      {filterable && items.length > 0 ? (
        <div className="grid gap-2 border-b border-border px-3 py-2" data-annot="фильтры референсов">
          <Segmented<VerdictFilter>
            label="вердикт"
            value={verdictFilter}
            onPick={setVerdictFilter}
            options={[
              { value: "all", label: `все ${num(verdictCounts.all)}` },
              { value: "works", label: `работает ${num(verdictCounts.works)}` },
              {
                value: "fails",
                label: `не работает ${num(verdictCounts.fails)}`,
              },
              { value: "none", label: `не оценён ${num(verdictCounts.none)}` },
            ]}
          />
          <Segmented<UsedFilter>
            label="генерация"
            value={usedFilter}
            onPick={setUsedFilter}
            options={[
              { value: "all", label: `все ${num(used.all)}` },
              { value: "yes", label: `была ${num(used.yes)}`, title: "С референсом уже запускали генерацию" },
              { value: "no", label: `не было ${num(used.no)}`, title: "С референсом ещё не генерировали" },
            ]}
          />
          <div className="flex flex-wrap items-center gap-1.5" data-annot="фильтр по тегу">
            <span className="text-[11.5px] text-fg-faint">тег</span>
            <Chip on={tagFilter === null} onClick={() => setTagFilter(null)}>
              все
            </Chip>
            <Chip on={tagFilter === "none"} onClick={() => setTagFilter(tagFilter === "none" ? null : "none")}>
              без тега <span className="tnum text-fg-faint">{num(untagged)}</span>
            </Chip>
            {autoGroups.map((g) => {
              const key = `g${g.id}` as const;
              return (
                <Chip
                  key={key}
                  on={tagFilter === key}
                  onClick={() => setTagFilter(tagFilter === key ? null : key)}
                  title={`Фото, у которых нет ни одного тега группы «${g.name}» — ни вручную, ни авто; новые фото без вектора разметятся после library embed`}
                >
                  {g.name}: без тега{" "}
                  <span className="tnum text-fg-faint">{num(groupMissingCounts.get(g.id) ?? 0)}</span>
                </Chip>
              );
            })}
            {tags
              .filter((t) => (tagCountsInSlice.get(t.id) ?? 0) > 0 || tagFilter === t.id)
              .map((t) => (
                <Chip key={t.id} on={tagFilter === t.id} onClick={() => setTagFilter(tagFilter === t.id ? null : t.id)}>
                  <span className="inline-flex items-center gap-1">
                    <span className="size-2 rounded-full" style={tagStyle(t.id)} />
                    {t.name} <span className="tnum text-fg-faint">{num(tagCountsInSlice.get(t.id) ?? 0)}</span>
                  </span>
                </Chip>
              ))}
            <button type="button" className={`${btnGhost} ml-auto`} onClick={() => setManaging(true)}>
              <Pencil size={12} />
              править теги
            </button>
          </div>
          {showMarks && markCounts ? (
            <Segmented<Marks>
              label="метки"
              value={marks}
              onPick={setMarks}
              annot="фильтр меток"
              options={[
                { value: "all", label: `все ${num(markCounts.all)}` },
                { value: "manual", label: `ручные ${num(markCounts.manual)}`, title: "Тег повешен человеком" },
                { value: "auto", label: `авто ${num(markCounts.auto)}`, title: "Тег поставил автотег по промпту" },
              ]}
            />
          ) : null}
        </div>
      ) : null}

      {modelKey && knownModel && items.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2" data-annot="шапка папки">
          <button type="button" className={btnGhost} onClick={toFolders}>
            ← Все модели
          </button>
          <span className="text-[12.5px]">
            {knownModel.name}
            {knownModel.collection ? ` · ${knownModel.collection}` : ""} · {num(openFolder?.items.length ?? 0)} фото
          </span>
        </div>
      ) : null}
      {modelKey && knownModel && modelItems.length > 0 ? (
        <FolderGenerations key={modelKey} items={modelItems} />
      ) : null}

      {items.length === 0 ? (
        <Empty>{emptyText}</Empty>
      ) : view === "sites" && !openSite ? (
        sitesQuery.isPending ? (
          <Empty>Загружаю площадки…</Empty>
        ) : sitesQuery.isError || !sitesData ? (
          <Empty>Не удалось загрузить площадки.</Empty>
        ) : siteNumber !== null ? (
          <Empty>
            <div className="grid justify-items-center gap-2">
              Такой площадки на этом уровне нет
              <button type="button" className={btnGhost} onClick={toSites}>
                ← все площадки
              </button>
            </div>
          </Empty>
        ) : sitesData.sites.length === 0 && sitesData.closeups === 0 ? (
          <Empty>Площадки ещё не считались: ./reels-runner library sites --library {libraryId}</Empty>
        ) : sitesData.sites.length === 0 ? (
          <Empty>
            Площадок нет: в библиотеке только {num(sitesData.closeups)}{" "}
            {plural(sitesData.closeups, "крупный план", "крупных плана", "крупных планов")}
          </Empty>
        ) : (
          <div
            className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 p-3"
            data-annot="площадки библиотеки"
          >
            {foundSites(sitesData.sites, itemsById).map(({ site, found }) => {
              const cover = (site.cover_id !== null ? itemsById.get(site.cover_id) : undefined) ?? found[0];
              return (
                <SiteTile
                  key={site.number}
                  site={site}
                  cover={cover}
                  count={found.length}
                  onOpen={() => openSiteNumber(site.number)}
                />
              );
            })}
          </div>
        )
      ) : modelKey && !knownModel ? (
        <Empty>
          <div className="grid justify-items-center gap-2">
            {scores ? "Такой модели в выдаче нет" : "Такой модели в библиотеке нет"}
            <button type="button" className={btnGhost} onClick={toFolders}>
              ← Все модели
            </button>
          </div>
        </Empty>
      ) : gridItems.length === 0 && !gridFolders?.length ? (
        <Empty>Под фильтр не попал ни один референс.</Empty>
      ) : (
        <div ref={gridRoot}>
          <ReferenceGrid
            key={gridKey}
            items={gridItems}
            {...(gridFolders ? { folders: gridFolders } : {})}
            {...(back ? { startLimit: back.limit } : {})}
            onOpenFolder={openModel}
            {...(scores ? { scores } : {})}
            percent={percent}
            selected={selected}
            selecting={selecting}
            onToggle={toggle}
            tagNames={tagNames}
            showLibrary={mode !== "library"}
            onBasket={async (id, add) => {
              await basket.mutateAsync({ ids: [id], add });
            }}
            onDelete={async (id) => {
              await remove.mutateAsync([id]);
            }}
            onVerdict={async (id, verdict) => {
              await verdictMut.mutateAsync({ ids: [id], verdict });
            }}
          />
        </div>
      )}

      {selected.size > 0 ? (
        <ReferenceSelectionBar
          count={selected.size}
          visibleCount={shown.length}
          foldersOnly={outsideLoose(selected, gridFolders, gridItems)}
          generate={{ to: generateHref(selected, shown, items), max: MAX_PAIRS }}
          onSelectVisible={selectVisible}
          onClear={onClear}
          basketMode={basketMode}
          onBasket={async () => {
            await basket.mutateAsync({ ids, add: basketMode === "add" });
          }}
          idsText={idsText(ids)}
          onCopyIds={() => copyText(idsText(ids))}
          onCsv={() => downloadBlob(itemsCsv(chosen, tagNames, location.origin), csvName(chosen.length))}
          onDelete={async () => {
            await remove.mutateAsync(removeIds);
          }}
          toDelete={toDelete}
          tags={tags}
          tagCounts={selectedTagCounts}
          onTag={async (tagId) => {
            await tagMut.mutateAsync({ ids, by: { tagId } });
          }}
          onCreateTag={async (name) => {
            await tagMut.mutateAsync({ ids, by: { name } });
          }}
          onUntag={async (tagId) => {
            await untagMut.mutateAsync({ ids, tagId });
          }}
          onVerdict={async (verdict) => {
            await verdictMut.mutateAsync({ ids, verdict });
          }}
          onMove={async (libraryId) => {
            await move.mutateAsync({ ids, libraryId });
          }}
          onCopy={async (libraryId) => {
            await copy.mutateAsync({ ids, libraryId });
          }}
          lastLibraryId={lastLibraryId}
          busy={
            basket.isPending ||
            remove.isPending ||
            tagMut.isPending ||
            untagMut.isPending ||
            verdictMut.isPending ||
            move.isPending ||
            copy.isPending
          }
          error={basket.error ?? remove.error ?? tagMut.error ?? untagMut.error ?? verdictMut.error}
          result={result}
        />
      ) : null}

      <TagManager
        open={managing}
        onClose={() => setManaging(false)}
        onChanged={() => void queryClient.invalidateQueries({ queryKey: ["library"] })}
      />
    </Panel>
  );
}
